import { useState, useEffect, useCallback, useRef } from "react";
import { Chart } from "../home/chart/chart";
import { useApi } from "../home/apiService";
import { AreaPicker } from "../home/AreaPicker";
import { Prediction, TargetType } from "../home/chart/chartTypes";
import { Price } from "../models/price";
import { useLanguage } from "../../i18n/LanguageProvider";

// Admin-only "forecast vs actual" view for the Prognosnoggrannhet tab. It owns
// the data home.tsx normally owns (timestamps / predictions / prices) but lets
// an admin time-travel to a past generation via the date+hour picker inside
// <Chart validationMode />. The chart itself renders the exact forecast interval
// (no history lead-in) so predicted and actual prices line up for comparison.
//
// The fetch logic deliberately mirrors home.tsx's three fetchers rather than
// sharing a hook, to keep the just-stabilised customer page untouched. The zone
// (any country) and the model (standard / Pro) are picked in the header; only
// that zone's predictions and prices are fetched.
export const ForecastValidationChart = () => {
  const { t } = useLanguage();
  const api = useApi();

  // Electricity is the only asset.
  const commodity: TargetType = "electricity";
  const [forecastRange, setForecastRange] = useState<"7d" | "30d">("7d");
  const [area, setArea] = useState("SE3");
  const [model, setModel] = useState<string | null>(null);

  const [availableTimestamps, setAvailableTimestamps] = useState<string[]>([]);
  const [selectedGeneratedAt, setSelectedGeneratedAt] = useState<string | null>(null);
  // Mirror of selectedGeneratedAt readable inside the mount effect without adding
  // it as a dependency (which would refetch on every date change).
  const selectedGeneratedAtRef = useRef<string | null>(null);
  const [rawPredictions, setRawPredictions] = useState<Record<string, Prediction[]>>({});
  const [prices, setPrices] = useState<Record<string, Price[]>>({});
  const [loadingTimestamps, setLoadingTimestamps] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Fetch the actual price series around a generation. We pull a generous window
  // ([gen − 2d, gen + 32d]) so both the 7d and 30d forecast intervals are fully
  // covered; <Chart> clips the prices to the exact forecast window.
  const fetchPrices = useCallback(async (generatedAt: string, zone: string) => {
    setLoadingPrices(true);
    try {
      const gen = new Date(generatedAt);
      const startTime = new Date(gen.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString();
      const endTime = new Date(gen.getTime() + 32 * 24 * 60 * 60 * 1000).toISOString();
      const pricesData: Record<string, Price[]> = {};
      try {
        const res = await api.fetchElectricityPrices(startTime, endTime, zone);
        if (res.prices) pricesData[zone] = res.prices;
      } catch (error) {
        console.error(`Error fetching prices for ${zone}:`, error);
      }
      setPrices(pricesData);
    } finally {
      setLoadingPrices(false);
    }
  }, [api]);

  const fetchPredictions = useCallback(async (
    generatedAt: string, range: "7d" | "30d", zone: string, mdl: string | null,
  ) => {
    setLoadingPredictions(true);
    try {
      let preds: any[] = [];
      try {
        const res = await api.fetchElectricityPredictions(zone, generatedAt, range, mdl);
        preds = res.predictions || [];
      } catch (error) {
        console.error(`Error fetching predictions for ${zone}:`, error);
      }
      setRawPredictions({ [zone]: preds.map((p: any) => ({ ...p, area: p.area || zone })) });
    } finally {
      setLoadingPredictions(false);
    }
  }, [api]);

  // On mount and whenever the zone or model changes: load that series' list of
  // generations, keep the one closest to the previously selected time (else the
  // latest), and fetch its predictions + prices.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingTimestamps(true);
      setLoadingPredictions(true);
      setLoadingPrices(true);
      try {
        const res = await api.fetchElectricityTimestamps(area, model);
        const timestamps: string[] = res.timestamps || [];
        if (cancelled) return;
        setAvailableTimestamps(timestamps);
        if (timestamps.length > 0) {
          const prev = selectedGeneratedAtRef.current;
          const prevMs = prev ? new Date(prev).getTime() : NaN;
          const keep = isNaN(prevMs)
            ? timestamps[0]
            : timestamps.reduce((best, ts) =>
                Math.abs(new Date(ts).getTime() - prevMs) < Math.abs(new Date(best).getTime() - prevMs) ? ts : best,
              timestamps[0]);
          setSelectedGeneratedAt(keep);
          selectedGeneratedAtRef.current = keep;
          await Promise.all([
            fetchPredictions(keep, forecastRange, area, model),
            fetchPrices(keep, area),
          ]);
        } else {
          setSelectedGeneratedAt(null);
          selectedGeneratedAtRef.current = null;
          setRawPredictions({});
          setPrices({});
          setLoadingPredictions(false);
          setLoadingPrices(false);
        }
      } catch (error) {
        console.error("Error fetching forecast timestamps:", error);
      } finally {
        if (!cancelled) setLoadingTimestamps(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, model]);

  const handleGeneratedAtChange = useCallback((timestamp: string) => {
    setSelectedGeneratedAt(timestamp);
    selectedGeneratedAtRef.current = timestamp;
    fetchPredictions(timestamp, forecastRange, area, model);
    fetchPrices(timestamp, area);
  }, [fetchPredictions, fetchPrices, forecastRange, area, model]);

  const handleForecastRangeChange = useCallback((range: "7d" | "30d") => {
    setForecastRange(range);
    if (selectedGeneratedAt) fetchPredictions(selectedGeneratedAt, range, area, model);
  }, [fetchPredictions, selectedGeneratedAt, area, model]);

  return (
    <div className="forecast-validation" style={{ marginBottom: 24 }}>
      <div className="sp-filterbar">
        <span className="sp-filterbar__label">{t("analytics.forecastVsActual")}</span>
        <div style={{ marginLeft: "auto" }}>
          <AreaPicker
            selectedArea={area}
            onChange={setArea}
            model={model}
            onModelChange={setModel}
            canViewPro
          />
        </div>
      </div>

      <Chart
        rawPredictions={rawPredictions}
        loadingPredictions={loadingPredictions}
        prices={prices}
        loadingPrices={loadingPrices}
        commodity={commodity}
        availableTimestamps={availableTimestamps}
        loadingTimestamps={loadingTimestamps}
        selectedGeneratedAt={selectedGeneratedAt}
        onGeneratedAtChange={handleGeneratedAtChange}
        forecastRange={forecastRange}
        onForecastRangeChange={handleForecastRangeChange}
        canViewExtended={model == null}
        selectedArea={area}
        onAreaChange={setArea}
        validationMode={true}
      />
    </div>
  );
};
